// Contract Change reference rules and 409 wording for Customer Management.
//
// The backend is the authority. prepare_staff_preview
// (backend/shared/contract_change_intent.py) decides which of the three
// references an action needs; this module only mirrors that rule so staff
// see it before they press Preview change:
//   * cancel: all three optional. A blank one is recorded as "Not supplied"
//     (design v3.1 AC-4).
//   * every other action (amend, extension, renew, replace, adjustment,
//     withdraw, finalize_cancel, and a due activation of a scheduled one):
//     proposed signed agreement and commercial approval are required.
//     The current signed agreement reference is required only when no
//     Contract State reference is recorded yet; once one is, the backend
//     reuses it and a blank field is fine.
// agreementHistory is read only from recorded Contract State rows, so an
// empty history means no reference is recorded.

export const CONTRACT_REFERENCE_FIELDS = [
  { name:"currentSourceAgreementReference",
    label:"Current signed agreement reference" },
  { name:"signedAgreementReference",
    label:"Proposed signed agreement reference" },
  { name:"commercialApprovalReference",
    label:"Commercial approval reference" },
];

export const hasRecordedSourceReference = context =>
  (context?.agreementHistory || []).length > 0;

export const requiredContractReferences = (changeType, recordedSource) =>
  changeType === "cancel" ? [] : [
    ...(recordedSource ? [] : ["currentSourceAgreementReference"]),
    "signedAgreementReference", "commercialApprovalReference",
  ];

// Whitespace-only counts as blank, as it does for the backend.
export const missingContractReferences = (form, required) =>
  required.filter(name => !String(form?.[name] ?? "").trim());

export const missingReferencesMessage = names => {
  const labels = CONTRACT_REFERENCE_FIELDS
    .filter(item => names.includes(item.name)).map(item => item.label);
  return `Fill in the required ${labels.length === 1 ? "reference" :
    "references"} before previewing: ${labels.join(", ")}.`;
};

const CONTRACT_CHANGE_CODE_MESSAGES = {
  attestation_reference_required:
    "A required reference is missing. Fill in every required reference, then preview again.",
  attestation_reference_invalid:
    "A reference is blank or contains unsupported characters. Use 1 to 500 printable characters, then preview again.",
  scheduled_contract_change_exists:
    "This customer already has a pending contract change. Resolve it before previewing another change.",
  provider_session_closure_unavailable:
    "Booking history check couldn't complete; try again in a minute. If it repeats, contact engineering with this code.",
};

// The backend's code always stays visible: staff quote it to engineering.
export const contractChangeErrorText = (exc, fallback) => {
  const code = exc?.body?.code;
  const generic = exc?.message || fallback;
  if (typeof code !== "string" || !code) return generic;
  return `${CONTRACT_CHANGE_CODE_MESSAGES[code] || generic} (code: ${code})`;
};

// A refresh that fails after a recorded confirmation. The write succeeded,
// so the recorded wording never changes; a code is only appended.
export const refreshFailureText = exc => {
  const code = exc?.body?.code;
  const recorded = "Recorded, but the refreshed view is unavailable. Reload to see current facts.";
  return typeof code === "string" && code
    ? `${recorded} (refresh code: ${code})` : recorded;
};
